import { Injectable } from '@angular/core';
import { 
  HttpClient, 
  HttpParams, 
  HttpHeaders,
  HttpResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
//import { getMockListForDate } from './cash-reconciliation.mock';
import { LoggerService } from '../../../services/logger.service';
import { environment } from '../../../../environments/environment';
import { getAuthHeadersFromUserData } from '../../../shared/utils/auth-headers.util';
import { transformFixResponse, transformReportResponse, mapAttendanceCashResponse } from './utils/cash-reconciliation.mapper';
import { recomputeEntry } from './helpers/reconciliation.helper';

import { 
  CashReconciliationEntry, 
  CashReconciliationUpdatePayload, 
  FixReconciliationEntry, 
  FixReconciliationEmployee, 
  AttendanceCashReconciliationResponse, 
  CashReconciliationReportResponse 
} from './interfaces/cash-reconciliation.interface';

@Injectable({ providedIn: 'root' })
export class CashReconciliationService {
  constructor(
    private http: HttpClient,
    private logger: LoggerService
  ) {}

  private readonly fixEntryStore = new Map<string, FixReconciliationEntry>();
  private listUrl = `${environment.baseUrl}/${environment.apiVersion}/employee/cash-reconciliation`;

  private handleError(error: any, api: string) {
    this.logger.error(`API Error - ${api}`, error, 'CashReconciliationService');
    return throwError(() => error);
  }

  /** Authorization + JSON Content-Type; prefers impersonation token when present. */
  private getAuthHeaders(): HttpHeaders {
    return getAuthHeadersFromUserData({
      includeJsonContentType: true
    });
  }

  private parseLocalStorageToken(key: string, label: string): string | null {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed?.token || null;
    } catch (error) {
      this.logger.error(`Invalid JSON in localStorage ${label}:`, error, 'ApiService');
      localStorage.removeItem(key);
      return null;
    }
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    const token = this.parseLocalStorageToken('userData', 'userData');
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    return headers;
  }
  /** GET /cash-reconciliation/all?default_date=&limit=&page= */
  getList(
    isoDate: string,
    page = 1,
    limit = 10
  ): Observable<{ entries: CashReconciliationEntry[]; totalItems: number }> {
    return this.getEntries(isoDate, page, limit);
  }

  getEntries(
    isoDate: string,
    page = 1,
    limit = 10
  ): Observable<{ entries: CashReconciliationEntry[]; totalItems: number }> {
    const params = new HttpParams()
      .set('default_date', isoDate)
      .set('page', String(Math.max(1, page)))
      .set('limit', String(Math.min(100, Math.max(1, limit))));
    const headers = this.getAuthHeaders();
    return this.http
      .get<any>(`${this.listUrl}/all`, { params, headers })
      .pipe(
        map((res) => this.transformResponse(res)),
        catchError(error => this.handleError(error, 'getEntries'))
      );
  }

  private transformResponse(apiRes: any): {
    entries: CashReconciliationEntry[];
    totalItems: number;
  } {
    if (!apiRes || apiRes.status === false) {
      return { entries: [], totalItems: 0 };
    }

    const list = apiRes?.data || [];
    const totalItems = Number(
      apiRes?.pagination?.total_records ?? list.length
    );
    const entries = list.map((item: any): CashReconciliationEntry => {
      const opening = Number(item.opening_balance ?? 0);
      const extra = Number(item.extra_cash_total ?? 0);
      const payment = Number(item.payment_received ?? 0);
      return {
        id: String(item.id),
        employee: {
          id: String(item.employee?.id || ''),
          name: item.employee?.name || '—',
          employee_code: item.employee?.employee_code || '',
          avatar_url: item.employee?.avatar_url || null
        },
        status: item.status,
        shift_id: item.shift.id ?? null,
        shift: item.shift.name ?? '--',
        location: item.location
          ? {
              id: String(item.location.id),
              name: item.location.name
            }
          : null,
        opening_balance: opening,
        opening_status: item.opening_status,
        extra_cash: (item.extra_cash || []).map((cash: any) => ({
          amount: Number(cash.amount ?? 0),
          time: cash.timestamp
        })),
        extra_cash_total: extra,
        payment_received: payment,
        expected_closing: Number(item.expected_closing),
        closing_balance: Number(item.closing_balance),
        delta: Number(item.delta),
        match_status: item.match_status,
        mismatch_reason: item.mismatch_reason,
        is_grayed: item.is_grayed || false
      };
    });

    return { entries, totalItems };
  }

  updateEntry(
    id: string,
    payload: CashReconciliationUpdatePayload
  ): Observable<CashReconciliationEntry> {
  
    return this.http.patch<CashReconciliationEntry>(
      `${this.listUrl}/${id}`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  bulkUpdate(
    payload: {
      reconciliation_id: string;
      opening_balance?: number | null;
      closing_balance?: number | null;
    }[]
  ): Observable<any> {
    return this.http.post(
      `${this.listUrl}/bulk`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }
  
  addExtraCash(
    id: string,
    payload: { amount: number }
  ): Observable<FixReconciliationEntry> {
  
    const body = {
      extra_money: [
        {
          amount: payload.amount,
          timestamp: new Date().toISOString()
        }
      ]
    };
  
    return this.http
      .patch<any>(`${this.listUrl}/${id}`, body, {headers: this.getAuthHeaders()})
      .pipe(
        map(res => {
          //depends on your API response shape
          const updated = res?.data ?? res;
          return updated;
        }),
        catchError(error => this.handleError(error, 'addExtraCash'))
      );
  }

  getAttendanceCash(
    isoDate: string
  ): Observable<AttendanceCashReconciliationResponse> {
    const headers = this.getAuthHeaders();

    return this.http
      .get<any>(`${this.listUrl}/my/orders`, {
        headers,
        params: {
          currentDate: isoDate
        }
      })
      .pipe(
        map((res) => {
          return mapAttendanceCashResponse(res)
        }),
        catchError(error => this.handleError(error, 'getAttendanceCash'))
      );
  }

  getTransactions(employeeId: string, date: string) {
    const params = new HttpParams().set('currentDate', date);
  
    return this.http.get<any>(
      `${this.listUrl}/${employeeId}/orders`,
      {
        params,
        headers: this.getAuthHeaders()
      }
    );
  }

  /** GET /cash-reconciliation/fix */
  getFixData(
    startDateUtc: string,
    endDateUtc: string,
    employeeIds: string[]
  ): Observable<FixReconciliationEmployee[]> {
  
    const body = {
      startDate: startDateUtc,
      endDate: endDateUtc,
      employee_ids: employeeIds
    };
  
    return this.http.post<any>(
      `${this.listUrl}/report`,
      body,
      { headers: this.getAuthHeaders() }
    ).pipe(
      map(res => transformFixResponse(res?.data || [])),
      catchError(error => this.handleError(error, 'getFixData'))
    );
  }

  getReport(
    q:
      | {
          type: 'daily';
          dateUtc: string;
          employee_ids?: string[];
        }
      | {
          type: 'monthly';
          month: string;
          year: string;
          employee_ids?: string[];
        }
      | {
          type: 'custom';
          startUtc: string;
          endUtc: string;
          employee_ids?: string[];
        }
  ): Observable<CashReconciliationReportResponse> {
  
    let startDate = '';
    let endDate = '';
  
    if (q.type === 'daily') {
      startDate = q.dateUtc;
      endDate = q.dateUtc;
    }
  
    else if (q.type === 'monthly') {
      const year = Number(q.year);
      const month = Number(q.month);
  
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
  
      startDate = this.toYMD(firstDay);
      endDate = this.toYMD(lastDay);
    }
  
    else {
      startDate = q.startUtc;
      endDate = q.endUtc;
    }
  
    const body: {
      startDate: string;
      endDate: string;
      employee_ids?: string[];
    } = {
      startDate,
      endDate,
    };
  
    // add only when available
    if (q.employee_ids?.length) {
      body.employee_ids = q.employee_ids;
    }
  
    return this.http.post<any>(
      `${this.listUrl}/report`,
      body,
      { headers: this.getAuthHeaders() }
    ).pipe(
      map(res =>
        transformReportResponse(
          res?.data || [],
          startDate,
          endDate
        )
      ),
      catchError(error => this.handleError(error, 'getReport'))
    );
  }

  private toYMD(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** In-place recompute (delta, status labels, status_key) — used by results grid on edit. */
  recomputeFixEntryInPlace(entry: FixReconciliationEntry): void {
    this.recomputeFixEntry(entry);
  }
  
  recomputeFixEntry(entry: FixReconciliationEntry): FixReconciliationEntry {
    return recomputeEntry(entry);
  }

  downloadReportPdf(payload: {
    startDate: string;
    endDate: string;
    employee_ids?: string[];
  }): Observable<HttpResponse<Blob>> {
    const body: any = {
      startDate: payload.startDate,
      endDate: payload.endDate
    };
    // add only if available
    if (payload.employee_ids?.length) {
      body.employee_ids = payload.employee_ids;
    }

    return this.http.post(
      `${this.listUrl}/report/download`,
      payload,
      {
        headers: this.getAuthHeaders(),
        responseType: 'blob',
        observe: 'response'
      }
    );
  }
}
